import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useId, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { dayWords } from '../penalties/penalties.js';
import {
  BAN_NOTE_MAX,
  BAN_REASON_LABEL,
  type BanReason,
  type UnpaidPenalty,
  penaltyLine,
} from './banReason.js';

/**
 * 381 (#1271, design "Ban reason" 1/1b): banning asks why. "Didn't pay a penalty" picks one of the customer's unpaid
 * penalties (none: the option is disabled and says to record it first); "Other" needs a note. The platform checks it all
 * again; this dialog only stops a ban that cannot be confirmed.
 */
export function BanDialog({
  userId,
  userName,
  unpaidPenalties,
  onClose,
}: {
  userId: string;
  userName: string;
  unpaidPenalties: UnpaidPenalty[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<BanReason | null>(null);
  const [penaltyId, setPenaltyId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const noteId = useId();
  const noPenalty = unpaidPenalties.length === 0;

  const ban = useMutation({
    mutationFn: () =>
      api.patch(`/users/${userId}/status`, {
        status: 'banned',
        ban_reason: reason,
        ...(reason === 'unpaid_penalty' && penaltyId ? { penalty_id: penaltyId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.one(userId) }),
        queryClient.invalidateQueries({ queryKey: ['user-events', userId] }),
      ]);
      onClose();
    },
  });

  const trimmed = note.trim();
  const valid =
    reason !== null &&
    trimmed.length <= BAN_NOTE_MAX &&
    (reason !== 'unpaid_penalty' || penaltyId !== null) &&
    (reason !== 'other' || trimmed.length > 0);

  const option = (value: BanReason, extra?: ReactNode, disabled = false) => (
    <label
      className={`flex gap-3 rounded-lg border p-3 ${
        reason === value ? 'border-primary bg-primary/5' : 'border-hairline'
      } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
    >
      <input
        type="radio"
        name="ban-reason"
        value={value}
        checked={reason === value}
        disabled={disabled}
        // The penalty is sent only with "Didn't pay a penalty", so a pick survives switching away and back.
        onChange={() => setReason(value)}
        className="mt-1"
      />
      <span className="flex-1">
        <span className="text-ink">{BAN_REASON_LABEL[value]}</span>
        {extra}
      </span>
    </label>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={`Ban ${userName}?`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={!valid || ban.isPending}
            loading={ban.isPending}
            onClick={() => ban.mutate()}
          >
            Ban account
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="ban-dialog">
        <p className="text-sm text-steel">
          They can still sign in and read, but can't book or change anything. Choose why.
        </p>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium text-ink">Reason</legend>
          {option(
            'unpaid_penalty',
            noPenalty ? (
              <span
                data-testid="ban-no-penalty"
                className="mt-1 block rounded-md bg-semantic-warning-bg px-2 py-1 text-xs text-semantic-warning-text"
              >
                No unpaid penalty for this customer. Record it on the Penalties page first.
              </span>
            ) : (
              <span className="block text-xs text-muted">Choose the unpaid penalty</span>
            ),
            noPenalty,
          )}
          {reason === 'unpaid_penalty' && (
            <div className="ml-7 flex flex-col gap-1.5" data-testid="ban-penalties">
              {unpaidPenalties.map((p) => (
                <label
                  key={p.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                    penaltyId === p.id ? 'border-primary' : 'border-hairline'
                  }`}
                >
                  <input
                    type="radio"
                    name="ban-penalty"
                    value={p.id}
                    checked={penaltyId === p.id}
                    onChange={() => setPenaltyId(p.id)}
                  />
                  <span>
                    {penaltyLine(p)} · {dayWords(p.on)}
                  </span>
                </label>
              ))}
            </div>
          )}
          {option('accident')}
          {option('other', <span className="block text-xs text-muted">A note is required</span>)}
        </fieldset>
        <div className="flex flex-col gap-1">
          <label htmlFor={noteId} className="text-sm font-medium text-ink">
            Note{' '}
            <span className="font-normal text-muted">
              {reason === 'other' ? '(required)' : '(optional)'}
            </span>
          </label>
          <Textarea
            id={noteId}
            rows={3}
            value={note}
            maxLength={BAN_NOTE_MAX}
            placeholder="Anything staff should know later"
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="text-right text-xs text-muted">
            {trimmed.length} / {BAN_NOTE_MAX}
          </span>
        </div>
        {ban.isError && (
          <p role="alert" className="text-sm text-semantic-danger-text">
            {(ban.error as { message?: string }).message ?? 'Could not ban the account.'}
          </p>
        )}
      </div>
    </Modal>
  );
}
